/**
 * Timesheet Settings Types
 * Defines organisation-level and employee-level timesheet configuration
 */

export interface TimesheetDeadlineSettings {
  /**
   * Day of week when timesheets are due (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
   * Default: 5 (Friday)
   */
  deadlineDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  
  /**
   * Time of day when timesheets are due (24-hour format)
   * Default: '17:00' (5:00 PM)
   */
  deadlineTime: string;
  
  /**
   * Grace period in days after deadline before marking as "late"
   * Default: 2 days
   */
  gracePeriodDays: number;
  
  /**
   * Whether to show feed notifications before the submission deadline
   * Default: true
   */
  sendReminders: boolean;
  
  /**
   * Whether to allow sending email reminders from the Missing Timesheet Report
   * Default: true
   */
  sendFeedReminders: boolean;
  
  /**
   * Days before deadline to send first reminder
   * Default: 2 days
   */
  reminderDaysBefore: number;
  
  /**
   * Enable 'Copy Previous Week' button
   * Default: true
   */
  enableCopyPreviousWeek: boolean;
  
  /**
   * Include entry descriptions when copying
   * Default: true
   */
  copyDescriptions: boolean;
  
  /**
   * Copy only billable entries when copying
   * Default: false
   */
  copyOnlyBillable: boolean;
  
  /**
   * Enable auto smart fill feature
   * Default: true
   */
  enableAutoSmartFill?: boolean;
  
  /**
   * Enable AI insights feature
   * Default: true
   */
  enableAiInsights?: boolean;
}

/**
 * Working day configuration with expected hours
 */
export interface WorkingDay {
  isWorkingDay: boolean;
  expectedHours: number;
  isCustom: boolean; // Track if user manually edited
}

/**
 * Working profile - single source of truth for working hours
 */
export interface WorkingProfile {
  /**
   * Standard hours per week (configurable: 35, 37.5, 40, 48, 20, etc.)
   */
  standardHoursPerWeek: number;
  
  /**
   * Daily breakdown (auto-calculated, but can be overridden)
   */
  workingDays: {
    monday: WorkingDay;
    tuesday: WorkingDay;
    wednesday: WorkingDay;
    thursday: WorkingDay;
    friday: WorkingDay;
    saturday: WorkingDay;
    sunday: WorkingDay;
  };
  
  /**
   * Optional notes about the working pattern
   */
  notes?: string;
  
  /**
   * Effective from date
   */
  effectiveFrom?: Date;
}

/**
 * Legal/policy limits (optional - only if needed)
 */
export interface PolicyLimits {
  /**
   * Legal maximum hours per day (e.g., 13h for EU Working Time Directive)
   * Optional - only configure if you have legal requirements
   */
  legalMaxHoursPerDay?: number;
  
  /**
   * Legal maximum hours per week (e.g., 48h for EU Working Time Directive)
   * Optional - only configure if you have legal requirements
   */
  legalMaxHoursPerWeek?: number;
}

export interface UtilisationSettings {
  /**
   * Target utilisation rate (0-1, e.g., 0.75 = 75%)
   * Default: 0.75
   */
  targetUtilisationRate: number;
  
  /**
   * Utilisation calculation method
   * - 'billable-total': Billable hours / Total hours worked
   * - 'billable-available': Billable hours / Available hours (excluding PTO)
   * - 'billable-expected': Billable hours / Expected hours (from working pattern)
   * Default: 'billable-total'
   */
  calculationMethod: 'billable-total' | 'billable-available' | 'billable-expected';
}

/**
 * Hours limit settings for time tracking validation
 */
export interface HoursLimitSettings {
  /**
   * Maximum hours allowed per day
   * Default: 12
   */
  dailyHoursLimit: number;
  
  /**
   * Maximum hours allowed per week
   * Default: 48
   */
  weeklyHoursLimit: number;
  
  /**
   * Whether to enforce hard limits (block submission) vs soft limits (warning only)
   * Default: false
   */
  enforceHardLimits: boolean;
}

export type PolicyCheckType = 
  | 'daily-overage'
  | 'weekly-overage'
  | 'legal-daily-maximum'
  | 'legal-weekly-maximum'
  | 'missing-hours'
  | 'non-working-day'
  | 'daily-missing-hours'
  | 'weekly-missing-hours'
  | 'daily-non-working-day'
  | 'weekly-non-working-day';

export type PolicySeverity = 'critical' | 'warning' | 'info';

export type DisplayMode = 'smart' | 'all' | 'critical-only';

export interface PolicyCheckConfig {
  enabled: boolean;
  severity: PolicySeverity;
  threshold?: number; // For overage checks (percentage over expected)
  enforcement?: 'warn' | 'block';
}

/**
 * Policy enforcement configuration - simplified version for working hours checks
 */
export interface PolicyEnforcement {
  checks: {
    DAILY_OVERAGE: PolicyCheckConfig;
    WEEKLY_OVERAGE: PolicyCheckConfig;
    LEGAL_MAXIMUM: PolicyCheckConfig;
    MISSING_HOURS: PolicyCheckConfig;
  };
  preset: 'strict' | 'balanced' | 'lenient' | 'custom';
}

export interface PolicyViolationSettings {
  /**
   * Monitoring frequency for policy checks
   * - 'day': Check hours on a daily basis
   * - 'week': Check hours on a weekly basis
   * Default: 'week'
   */
  monitoringFrequency: 'day' | 'week';
  
  /**
   * Option 1: Suppress daily warnings when weekly limit is exceeded
   * Prevents duplicate alerts for the same underlying issue
   * Default: true
   */
  suppressDailyWhenWeeklyExceeded: boolean;
  
  /**
   * Option 1: Consolidate multiple daily warnings into a single summary
   * Reduces alert fatigue when multiple days have similar issues
   * Default: true
   */
  consolidateDailyWarnings: boolean;
  
  /**
   * Option 2 & 3: Individual check configuration
   * Controls which checks are enabled and their severity
   */
  checks: Record<PolicyCheckType, PolicyCheckConfig>;
  
  /**
   * Option 3: Display mode for alerts
   * - 'smart': Apply suppression and consolidation rules
   * - 'all': Show all alerts regardless of settings
   * - 'critical-only': Show only critical severity alerts
   * Default: 'smart'
   */
  displayMode: DisplayMode;
  
  /**
   * Option 3: Whether to show info-level alerts
   * Default: true
   */
  showInfoAlerts: boolean;
  
  /**
   * Option 3: Preset configuration
   * - 'strict': All checks enabled, low thresholds
   * - 'balanced': Most checks enabled, moderate thresholds
   * - 'lenient': Fewer checks, higher thresholds
   * - 'custom': User-defined configuration
   * Default: 'balanced'
   */
  preset: 'strict' | 'balanced' | 'lenient' | 'custom';
}

export interface OrganisationTimesheetSettings {
  /**
   * Deadline configuration
   */
  deadline: TimesheetDeadlineSettings;
  
  /**
   * Working profile - single source of truth for working hours
   */
  workingProfile: WorkingProfile;
  
  /**
   * Policy limits (optional legal/regulatory limits)
   */
  policyLimits?: PolicyLimits;
  
  /**
   * Hours limit settings for validation
   */
  hoursLimits: HoursLimitSettings;
  
  /**
   * Utilisation configuration
   */
  utilisation: UtilisationSettings;
  
  /**
   * Policy violation alert settings
   */
  policyViolations: PolicyViolationSettings;
  
  /**
   * Week start day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
   * Default: 1 (Monday)
   */
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  
  /**
   * Whether to show and capture role on timesheet entries
   * When enabled, users can select a role for each entry (defaults from employee profile)
   * Default: false
   */
  showRole: boolean;
  
  /**
   * Whether role is required when showRole is enabled
   * Default: false
   */
  requireRole: boolean;
  
  /**
   * Whether to show and capture cost centre on timesheet entries
   * Default: true (for backward compatibility)
   */
  showCostCentre: boolean;
  
  /**
   * Whether cost centre is required when showCostCentre is enabled
   * Default: true
   */
  requireCostCentre: boolean;
  
  /**
   * Whether description is required on timesheet entries
   * Default: 'always'
   */
  requireDescription: 'never' | 'always';
  
  /**
   * Minimum time entry duration
   * Default: '6min'
   */
  minimumEntryDuration: '6min' | '15min' | '30min' | '1hour';
  
  /**
   * Time rounding rule applied to entered hours
   * Default: 'none'
   */
  timeRounding: 'none' | '15min' | '30min' | '1hour';
  
  /**
   * Whether negative hours are permitted for corrections
   * Default: false
   */
  allowNegativeHours: boolean;
  
  /**
   * How many weeks back employees can log time
   * Default: 4
   */
  historicalEntryLimit: number;
  
  /**
   * How far in the future employees can log time
   * Default: '2weeks'
   */
  allowFutureDatedEntries: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited';
  
  /**
   * Project selection scope- whether to show all projects or only assigned ones
   * Default: 'all'
   */
  projectSelectionScope: 'all' | 'assigned';
  
  /**
   * Whether to show archived projects in the project selector
   * Default: false
   */
  showArchivedProjects: boolean;
  
  /**
   * Whether to show remaining project time/budget in the project selector
   * Default: true
   */
  showProjectTimeRemaining: boolean;
  
  /**
   * Last updated timestamp
   */
  updatedAt?: Date;
  
  /**
   * Updated by user ID
   */
  updatedBy?: string;
}

/**
 * Default organisation settings
 */
export const DEFAULT_TIMESHEET_SETTINGS: OrganisationTimesheetSettings = {
  deadline: {
    deadlineDay: 5, // Friday
    deadlineTime: '17:00', // 5:00 PM
    gracePeriodDays: 2,
    sendReminders: true,
    sendFeedReminders: true,
    reminderDaysBefore: 2,
    enableCopyPreviousWeek: true,
    copyDescriptions: true,
    copyOnlyBillable: false
  },
  workingProfile: {
    standardHoursPerWeek: 40,
    workingDays: {
      monday: { isWorkingDay: true, expectedHours: 8.0, isCustom: false },
      tuesday: { isWorkingDay: true, expectedHours: 8.0, isCustom: false },
      wednesday: { isWorkingDay: true, expectedHours: 8.0, isCustom: false },
      thursday: { isWorkingDay: true, expectedHours: 8.0, isCustom: false },
      friday: { isWorkingDay: true, expectedHours: 8.0, isCustom: false },
      saturday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      sunday: { isWorkingDay: false, expectedHours: 0, isCustom: false }
    }
  },
  policyLimits: {
    legalMaxHoursPerDay: 13,
    legalMaxHoursPerWeek: 48
  },
  hoursLimits: {
    dailyHoursLimit: 12,
    weeklyHoursLimit: 48,
    enforceHardLimits: false
  },
  utilisation: {
    targetUtilisationRate: 0.75,
    calculationMethod: 'billable-total'
  },
  policyViolations: {
    monitoringFrequency: 'week',
    suppressDailyWhenWeeklyExceeded: true,
    consolidateDailyWarnings: true,
    checks: {
      'daily-overage': { enabled: true, severity: 'warning', threshold: 1.25 },
      'weekly-overage': { enabled: true, severity: 'warning', threshold: 1.10 },
      'legal-daily-maximum': { enabled: false, severity: 'critical' },
      'legal-weekly-maximum': { enabled: false, severity: 'critical' },
      'missing-hours': { enabled: true, severity: 'warning', threshold: 0.80 },
      'non-working-day': { enabled: true, severity: 'info' },
      'daily-missing-hours': { enabled: true, severity: 'warning', threshold: 0.80 },
      'weekly-missing-hours': { enabled: true, severity: 'warning', threshold: 0.80 },
      'daily-non-working-day': { enabled: true, severity: 'info' },
      'weekly-non-working-day': { enabled: true, severity: 'info' }
    },
    displayMode: 'smart',
    showInfoAlerts: true,
    preset: 'balanced'
  },
  weekStartDay: 1, // Monday
  showRole: false, // Role capture disabled by default
  requireRole: false,
  showCostCentre: true, // Cost centre enabled by default for backward compatibility
  requireCostCentre: true,
  requireDescription: 'always',
  minimumEntryDuration: '6min',
  timeRounding: 'none',
  allowNegativeHours: false,
  historicalEntryLimit: 4,
  allowFutureDatedEntries: '2weeks',
  projectSelectionScope: 'all',
  showArchivedProjects: false,
  showProjectTimeRemaining: true
};

/**
 * Helper to get day name from day number
 */
export function getDayName(day: number): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[day] || 'Unknown';
}

/**
 * Helper to get short day name from day number
 */
export function getShortDayName(day: number): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[day] || '???';
}

/**
 * Preset configurations for policy violation settings
 */
export const POLICY_PRESETS: Record<'strict' | 'balanced' | 'lenient', Omit<PolicyViolationSettings, 'preset'>> = {
  strict: {
    monitoringFrequency: 'week',
    suppressDailyWhenWeeklyExceeded: true,
    consolidateDailyWarnings: false,
    checks: {
      'daily-overage': { enabled: true, severity: 'critical', threshold: 1.10 },
      'weekly-overage': { enabled: true, severity: 'critical', threshold: 1.05 },
      'legal-daily-maximum': { enabled: true, severity: 'critical' },
      'legal-weekly-maximum': { enabled: true, severity: 'critical' },
      'missing-hours': { enabled: true, severity: 'critical', threshold: 0.90 },
      'non-working-day': { enabled: true, severity: 'warning' },
      'daily-missing-hours': { enabled: true, severity: 'critical', threshold: 0.90 },
      'weekly-missing-hours': { enabled: true, severity: 'critical', threshold: 0.90 },
      'daily-non-working-day': { enabled: true, severity: 'warning' },
      'weekly-non-working-day': { enabled: true, severity: 'warning' }
    },
    displayMode: 'all',
    showInfoAlerts: true
  },
  balanced: {
    monitoringFrequency: 'week',
    suppressDailyWhenWeeklyExceeded: true,
    consolidateDailyWarnings: true,
    checks: {
      'daily-overage': { enabled: true, severity: 'warning', threshold: 1.25 },
      'weekly-overage': { enabled: true, severity: 'warning', threshold: 1.10 },
      'legal-daily-maximum': { enabled: false, severity: 'critical' },
      'legal-weekly-maximum': { enabled: false, severity: 'critical' },
      'missing-hours': { enabled: true, severity: 'warning', threshold: 0.80 },
      'non-working-day': { enabled: true, severity: 'info' },
      'daily-missing-hours': { enabled: true, severity: 'warning', threshold: 0.80 },
      'weekly-missing-hours': { enabled: true, severity: 'warning', threshold: 0.80 },
      'daily-non-working-day': { enabled: true, severity: 'info' },
      'weekly-non-working-day': { enabled: true, severity: 'info' }
    },
    displayMode: 'smart',
    showInfoAlerts: true
  },
  lenient: {
    monitoringFrequency: 'week',
    suppressDailyWhenWeeklyExceeded: true,
    consolidateDailyWarnings: true,
    checks: {
      'daily-overage': { enabled: false, severity: 'info', threshold: 1.50 },
      'weekly-overage': { enabled: true, severity: 'info', threshold: 1.20 },
      'legal-daily-maximum': { enabled: false, severity: 'critical' },
      'legal-weekly-maximum': { enabled: false, severity: 'critical' },
      'missing-hours': { enabled: false, severity: 'info', threshold: 0.70 },
      'non-working-day': { enabled: false, severity: 'info' },
      'daily-missing-hours': { enabled: false, severity: 'info', threshold: 0.70 },
      'weekly-missing-hours': { enabled: false, severity: 'info', threshold: 0.70 },
      'daily-non-working-day': { enabled: false, severity: 'info' },
      'weekly-non-working-day': { enabled: false, severity: 'info' }
    },
    displayMode: 'smart',
    showInfoAlerts: false
  }
};

/**
 * Get human-readable label for policy check type
 */
export function getPolicyCheckLabel(type: PolicyCheckType): string {
  const labels: Record<string, string> = {
    'daily-overage': 'Daily Hours Over Expected',
    'weekly-overage': 'Weekly Hours Over Expected',
    'legal-daily-maximum': 'Legal Daily Maximum Exceeded',
    'legal-weekly-maximum': 'Legal Weekly Maximum Exceeded',
    'missing-hours': 'Missing Expected Hours',
    'non-working-day': 'Work on Non-Working Day',
    'daily-missing-hours': 'Daily Missing Hours',
    'weekly-missing-hours': 'Weekly Missing Hours',
    'daily-non-working-day': 'Daily Non-Working Day Entry',
    'weekly-non-working-day': 'Weekly Non-Working Day Entry'
  };
  return labels[type] ?? type;
}

/**
 * Get description for policy check type
 */
export function getPolicyCheckDescription(type: PolicyCheckType): string {
  const descriptions: Record<string, string> = {
    'daily-overage': 'Alerts when daily hours exceed expected hours by threshold percentage (e.g., 125% of 8h = 10h)',
    'weekly-overage': 'Alerts when weekly hours exceed expected hours by threshold percentage (e.g., 110% of 40h = 44h)',
    'legal-daily-maximum': 'Alerts when daily hours exceed legal maximum (e.g., 13h for EU Working Time Directive)',
    'legal-weekly-maximum': 'Alerts when weekly hours exceed legal maximum (e.g., 48h for EU Working Time Directive)',
    'missing-hours': 'Alerts when hours are below expected for the period (e.g., below 80% of expected)',
    'non-working-day': 'Alerts when hours are logged on non-working days (weekends, holidays)',
    'daily-missing-hours': 'Alerts when daily hours are below expected for the day',
    'weekly-missing-hours': 'Alerts when weekly hours are below expected for the week',
    'daily-non-working-day': 'Alerts when hours are logged on non-working days (daily check)',
    'weekly-non-working-day': 'Alerts when hours are logged on non-working days (weekly check)'
  };
  return descriptions[type] ?? type;
}

/**
 * Get severity badge color
 */
export function getSeverityColor(severity: PolicySeverity): string {
  const colors: Record<PolicySeverity, string> = {
    critical: 'destructive',
    warning: 'warning',
    info: 'secondary'
  };
  return colors[severity];
}

/**
 * Get display mode description
 */
export function getDisplayModeDescription(mode: DisplayMode): string {
  const descriptions: Record<DisplayMode, string> = {
    smart: 'Apply suppression and consolidation rules to reduce alert fatigue',
    all: 'Show all alerts regardless of suppression settings',
    'critical-only': 'Show only critical severity alerts'
  };
  return descriptions[mode];
}
